import * as React from 'react';

import { RowHeaderCell } from './RowHeaderCell';
import { HeaderColumn } from './HeaderColumn';
import { DataGrid } from './DataGrid';
import { Dictionary } from './Utils';
import { GridSelection } from './GridSelection';

export class HeaderRow extends React.PureComponent<HeaderRow.Props> {
  public static displayName: string = 'DataGridHeaderRow';

  private measuredHeaderWidths: Dictionary<number> = {};

  public componentDidMount(): void {
    this.reportMeasuredColumnWidths();
  }

  public componentDidUpdate(): void {
    this.reportMeasuredColumnWidths();
  }

  private reportMeasuredColumnWidths(): void {
    if (this.props.measureColumnWidths && Object.keys(this.measuredHeaderWidths).length > 0) {
      const measuredHeaderWidths = { ...this.measuredHeaderWidths };
      this.measuredHeaderWidths = {};
      this.props.measureColumnWidths(measuredHeaderWidths);
    }
  }

  public handleReportHeaderWidth = (columnKey: string, width: number) => {
    if (this.props.measureColumnWidths) {
      this.measuredHeaderWidths[columnKey] = Math.max(width, this.measuredHeaderWidths[columnKey] || 0);
    }
  }

  private isColumnSelected(colIdx: number): boolean {
    return !!this.props.selectionModel && this.props.selectionModel.isSelected({ colIdx, colKey: this.props.cols[colIdx].dataKey, rowIdx: -1 });
  }

  public render(): JSX.Element {
    const columnWidths = this.props.columnWidths || {};

    // tslint:disable-next-line
    let hcols: React.ReactElement<any>[] = [];

    // Optionally show Select-All corner 'button'
    if (!!this.props.onRowHeaderCellClick && this.props.colStartIdx === 0) {
      hcols.push(
        <RowHeaderCell {...{
          key: `hcell`,
          isSelected: false,
          onRowHeaderCellClick: this.props.onRowHeaderCellClick,
          rowType: RowHeaderCell.RowType.Header,
          testId: 'hcell'
        }} />);
    }

    for (let colIdx = this.props.colStartIdx; colIdx <= this.props.colEndIdx; colIdx++) {
      const col = this.props.cols[colIdx];
      const shouldReportWidth = !col.preferredWidth && this.props.measuredColumnWidths[col.name] === undefined;

      hcols.push(<HeaderColumn {...{
        key: col.name,
        col: col,
        colIdx: colIdx,
        isSelected: this.isColumnSelected(colIdx),
        showVerticalGridLines: this.props.showVerticalGridLines,
        sorted: this.props.sortKey === col.dataKey,
        sortAscending: this.props.sortAscending,
        reportWidthFn: shouldReportWidth ? this.handleReportHeaderWidth : undefined,
        width: shouldReportWidth ? undefined : columnWidths[col.name],
        minWidth: this.props.minColWidth,
        onColumnResize: this.props.onColumnResize,
        onHeaderClick: this.props.onHeaderClick,
        onHeaderDoubleClick: this.props.onHeaderDoubleClick,
        onHeaderContextMenu: this.props.onHeaderContextMenu
      }} />);
    }

    return (
      <div aria-rowindex={1} className='tb-react-dg-hrow' role='row' style={{ height: this.props.headerHeight, paddingLeft: this.props.leftMargin }}>
        <div className='tb-react-dg-hcols' style={{ display: 'inline-block', height: '100%', marginLeft: -this.props.scrollLeft, whiteSpace: 'nowrap' }}>
          {hcols}
        </div>
      </div>
    );
  }
}

export namespace HeaderRow {

  export interface Props {
    rowCount: number;
    cols: DataGrid.Column[];
    colStartIdx: number;
    colEndIdx: number;
    columnWidths: DataGrid.ColumnWidths | null;
    minColWidth: number;
    leftMargin: number;
    measuredColumnWidths: Dictionary<number>;
    headerHeight: number;

    // tslint:disable-next-line:no-any
    measureColumnWidths?: (colWidths: Dictionary<number>) => any;

    showVerticalGridLines?: boolean;

    sortKey?: string;
    sortAscending: boolean;

    onRowHeaderCellClick?: (e: React.MouseEvent<HTMLDivElement>) => void;

    onHeaderClick?: DataGrid.ClickHandler;
    onHeaderDoubleClick?: DataGrid.ClickHandler;
    onHeaderContextMenu?: DataGrid.ClickHandler;

    // tslint:disable-next-line:no-any
    onColumnResize: (column: DataGrid.Column, width: number) => any;

    scrollLeft: number;

    selectionModel?: GridSelection;
    selectionTimestamp?: number;
  }

}
